/**
 * DashMessages — Phase 6
 * Business-side inbox for customer conversations. Also usable for
 * individual users (business_id filter is optional).
 */
import React, { useCallback, useEffect, useState } from "react";
import { MessageSquare, Send, RefreshCw, User } from "lucide-react";
import { toast } from "sonner";

import { useI18n } from "../../../lib/i18n";
import { useAuth } from "../../../lib/auth";
import { commsAPI } from "../../../lib/comms";
import { sameConvList, sameMessageList, useSmartAutoScroll } from "../../../lib/chatUtils";

export default function DashMessages({ businessId }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // Smart auto-scroll: only jumps to bottom when the user is already
  // near the bottom, when they just sent a message, or on first load.
  // Prevents the "dancing messages" bug where the 15s poll yanked the
  // user back down every cycle even when they were reading older msgs.
  const scrollRef = useSmartAutoScroll(messages, user?.id);

  /* `silent` = background poll → do NOT touch the loading state
     (previously flashed a `…` placeholder over the sidebar every 15s). */
  const loadConvs = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const data = await commsAPI.listConversations({ business_id: businessId, limit: 100 });
      const next = data.items || [];
      // Idempotent update — keeps the previous reference (and therefore
      // the DOM) when nothing meaningful has changed on this poll.
      setConvs((prev) => (sameConvList(prev, next) ? prev : next));
      setActiveId((prev) => prev || (next[0]?.id ?? null));
    } catch (e) {
      if (!silent) toast.error(lang === "ar" ? "تعذّر تحميل المحادثات" : "Failed to load conversations");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [businessId, lang]);

  const loadMessages = useCallback(async (id, { silent = false } = {}) => {
    if (!id) return;
    try {
      const data = await commsAPI.getMessages(id, { limit: 200 });
      const next = data.items || [];
      setMessages((prev) => (sameMessageList(prev, next) ? prev : next));
      await commsAPI.markRead(id).catch(() => {});
    } catch {
      if (!silent) toast.error(lang === "ar" ? "تعذّر تحميل الرسائل" : "Failed to load messages");
    }
  }, [lang]);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);

  // Silent polling — 15s cadence for both conversation list and messages
  // of the active chat. `silent: true` prevents loading flicker and toasts.
  useEffect(() => {
    const t = setInterval(() => {
      loadConvs({ silent: true });
      if (activeId) loadMessages(activeId, { silent: true });
    }, 15000);
    return () => clearInterval(t);
  }, [activeId, loadMessages, loadConvs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    setSending(true);
    try {
      await commsAPI.sendMessage({ conversation_id: activeId, text: text.trim() });
      setText("");
      // Refresh silently — we don't need loading spinners after a send.
      await loadMessages(activeId, { silent: true });
      await loadConvs({ silent: true });
    } catch (err) {
      toast.error(lang === "ar" ? "تعذّر إرسال الرسالة" : "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 rounded-2xl bg-white border border-[#0a0a0a]/[0.06] overflow-hidden h-[calc(100vh-260px)] min-h-[500px]"
         data-testid="dash-messages">
      {/* ── Conversation list ── */}
      <aside className="border-e border-[#0a0a0a]/[0.06] bg-[#fbfaf6] flex flex-col min-h-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#0a0a0a]/[0.06]">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" />
            {lang === "ar" ? "المحادثات" : "Conversations"}
          </div>
          <button type="button" onClick={() => loadConvs()} data-testid="msgs-refresh"
                  className="p-1 rounded-full hover:bg-[#0a0a0a]/[0.06]">
            <RefreshCw className="w-3.5 h-3.5 text-[#525252]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="p-6 text-center text-sm text-[#8a8a8a]">…</div>
          ) : convs.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#8a8a8a]" data-testid="msgs-empty">
              {lang === "ar" ? "لا توجد محادثات بعد" : "No conversations yet"}
            </div>
          ) : (
            convs.map((c) => {
              const unread = user ? Number(c.unread_by?.[user.id] || 0) : 0;
              const isActive = c.id === activeId;
              return (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  className={`w-full text-start px-4 py-3 border-b border-[#0a0a0a]/[0.04] transition-colors ${
                    isActive ? "bg-white" : "hover:bg-white/60"
                  }`}
                  data-testid={`conv-item-${c.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-[#0a0a0a]/[0.06] flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-[#525252]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[13px] font-semibold text-[#0a0a0a] truncate">
                          {c.kind === "buyer_seller" ? (lang === "ar" ? "محادثة" : "Chat") :
                           lang === "ar" ? "عميل" : "Customer"}
                        </div>
                        {unread > 0 && (
                          <span className="text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                            {unread}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#8a8a8a] truncate mt-0.5">
                        {c.last_message_preview || (lang === "ar" ? "بدء محادثة" : "New conversation")}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* ── Chat pane ── */}
      <section className="flex flex-col min-h-0">
        {!activeId ? (
          <div className="flex-1 flex items-center justify-center text-sm text-[#8a8a8a]">
            {lang === "ar" ? "اختر محادثة للعرض" : "Select a conversation"}
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#faf9f5] min-h-0">
              {messages.map((m) => {
                const mine = m.sender_id === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}
                       data-testid={`msg-${m.id}`}>
                    <div
                      className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                        mine
                          ? "bg-[#0a0a0a] text-[#f7eecd] rounded-br-md"
                          : "bg-white border border-[#0a0a0a]/10 text-[#0a0a0a] rounded-bl-md"
                      }`}
                    >
                      {m.text}
                      <div className={`text-[10px] mt-1 ${mine ? "text-[#f7eecd]/60" : "text-[#8a8a8a]"}`}>
                        {new Date(m.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} className="p-3 border-t border-[#0a0a0a]/[0.06] flex items-center gap-2 bg-white">
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={lang === "ar" ? "اكتب رسالتك…" : "Type a message…"}
                className="flex-1 px-4 py-2.5 rounded-full border border-[#0a0a0a]/15 text-sm focus:outline-none focus:border-[#c9a55c]"
                data-testid="msg-input"
              />
              <button
                type="submit"
                disabled={!text.trim() || sending}
                className="p-2.5 rounded-full disabled:opacity-40"
                style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}
                data-testid="msg-send"
              >
                <Send className="w-4 h-4 text-[#0a0a0a]" />
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
